/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { memo, useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import LoginIcon from '@mui/icons-material/Login';
import { Button, Link, Typography } from '@mui/material';

import { Endpoints, Infer } from 'shared';

import { OidcProviderButton } from '../../../../../../components/OidcProviderButton/OidcProviderButton';
import { SsoProviderButton } from '../../../../../../components/SsoProviderButton/SsoProviderButton';
import { BasicRoutes } from '../../../../../../constants/basicRoutes';
import { Namespaces } from '../../../../../../i18n';
import { AuthenticationApiService } from '../../../../../../services/AuthenticationApiService';
import { LoginMode } from '../../../../../../types/common/LoginMode';
import { createLoginPageStyles } from '../../../../LoginPage.styles';
import { useLoginOptionsStyles } from './LoginOptions.styles';

type LoginOptionsProps = {
  params: string;
  option?: Infer<typeof Endpoints.GetAuthenticationLoginOptions>['Response']['200'];
  email: string;
  handleLoginModeChange: (mode: LoginMode) => void;
};

export const LoginOptions = memo<LoginOptionsProps>(props => {
  const { params, option, email, handleLoginModeChange } = props;
  const { t } = useTranslation();
  const classes = useLoginOptionsStyles();
  const loginClasses = createLoginPageStyles();
  const navigate = useNavigate();
  const emailRef = useRef(email);
  emailRef.current = email;

  const [organizationsRedirects, setOrganizationsRedirects] = useState<Array<{ id: string; redirect: string }>>();

  useEffect(() => {
    if (option?.type === 'none') {
      const fetchOidcRedirects = async () => {
        try {
          const oidcRedirects = await Promise.all(
            option.organizations.map(async org => {
              const loginOidc = await AuthenticationApiService.loginOidc({
                organizationId: org.id,
                email: emailRef.current,
              });
              return { id: org.id, redirect: loginOidc.data.payload.redirect.href };
            }),
          );
          setOrganizationsRedirects(oidcRedirects);
        } catch (e) {
          console.error(e);
        }
      };

      void fetchOidcRedirects();
    }
  }, [option]);

  const getOrgRedirect = (id: string) => {
    return organizationsRedirects?.find(org => org.id === id)?.redirect ?? '';
  };

  const onCreateNewAccount = () => {
    void navigate({
      pathname: BasicRoutes.SIGN_UP,
      search: `?${params.toString()}`,
    });
  };

  switch (option?.type) {
    case 'basic':
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '15px' }}>
          <Typography textAlign="center">
            <Trans i18nKey={`${Namespaces.Login}:page.sso.options.basic`} components={{ b: <b /> }} />
          </Typography>
          <Button
            fullWidth
            variant="contained"
            onClick={() => handleLoginModeChange(LoginMode.Password)}
            startIcon={<LoginIcon />}
          >
            {t(`${Namespaces.Login}:page.sso.options.continue_with_basic`)}
          </Button>
        </div>
      );
    case 'oauth2':
      return <SsoProviderButton provider={option.provider} />;
    case 'none':
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          {!option.organizations.length ? (
            <Typography className={loginClasses.noneAnnotation} sx={{ mb: '15px' }}>
              <Trans
                i18nKey={t(`${Namespaces.Login}:page.sso.options.no_domain_match`)}
                components={{
                  l: <Link onClick={onCreateNewAccount} className={classes.ssoLink} />,
                }}
              />
            </Typography>
          ) : (
            <>
              <Typography className={loginClasses.noneAnnotation}>
                <Trans
                  i18nKey={`${Namespaces.Login}:page.sso.options.no_profile_match`}
                  components={{
                    l: <Link onClick={onCreateNewAccount} className={classes.ssoLink} />,
                  }}
                />
              </Typography>
              <div className={loginClasses.orgsList}>
                {option.organizations.map(org => (
                  <OidcProviderButton
                    key={org.id}
                    variant="contained"
                    redirect={getOrgRedirect(org.id)}
                    organizationName={org.name}
                    thumbnailSrc={org.thumbnail}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      );
    default:
      return null;
  }
});
